import React, { memo, useCallback, useMemo } from 'react';
import {
  Box, Button, Divider, IconButton, Paper, Stack, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { Add, ContentCopyOutlined, DeleteOutline } from '@mui/icons-material';

import HelpTip, { HelpContent } from '../../common/HelpTip';
import { GroupLabel, segmentedSx } from '../tabLayout';
import RubricItemRow from './RubricItemRow';
import { moveAt } from './sectionParts';
import { isScored, roundMax } from '../../../utils/admissionAssessment';
import type { InterviewRoundWithRubric, InterviewRubricItem } from '../../../types/admissions';

const TYPE_HELP: HelpContent = {
  title: 'What kind of round is this?',
  terms: [
    ['Interview', 'you talk with the child or parents and score their answers.'],
    ['Observation', 'you watch the child in an activity or at play and score what you see. Common for the youngest classes.'],
  ],
  footnote: 'Only the wording changes — sheets and printouts say Observation and Observed by.',
};

const MARKS_HELP: HelpContent = {
  title: 'How finely can questions be scored?',
  terms: [
    ['Whole', 'whole numbers only: 0, 1, 2 …'],
    ['Halves', 'half marks too, e.g. 1.5 out of 2.'],
  ],
  footnote: 'Applies to every marked question in this round. Choice and note questions carry no marks.',
};

interface Props {
  round: InterviewRoundWithRubric;
  index: number;
  onChange: (index: number, patch: Partial<InterviewRoundWithRubric>) => void;
  onRemove: (index: number) => void;
  /** Adds a copy of this round right after it. */
  onDuplicate: (index: number) => void;
}

/**
 * One interview round in the editor: its name, total and mark step, and the
 * questions on its sheet. Scored questions set the total; choice and note
 * questions ride along unscored.
 */
const InterviewRoundEditor = memo<Props>(({ round, index, onChange, onRemove, onDuplicate }) => {
  const rubric = useMemo(() => round.rubric || [], [round.rubric]);
  const scoredCount = rubric.filter(isScored).length;
  const scored = scoredCount > 0;

  const setRubric = useCallback(
    (next: InterviewRubricItem[]) => onChange(index, { rubric: next }),
    [index, onChange],
  );
  const changeItem = useCallback(
    (j: number, patch: Partial<InterviewRubricItem>) => setRubric(rubric.map((c, k) => (k === j ? { ...c, ...patch } : c))),
    [rubric, setRubric],
  );
  const removeItem = useCallback((j: number) => setRubric(rubric.filter((_, k) => k !== j)), [rubric, setRubric]);
  const moveItem = useCallback((j: number, dir: -1 | 1) => setRubric(moveAt(rubric, j, dir)), [rubric, setRubric]);
  // Every scored question takes this one's marks; choice and note stay unscored.
  const copyMarks = useCallback((j: number) => {
    const maxMarks = rubric[j].maxMarks;
    setRubric(rubric.map(c => (isScored(c) ? { ...c, maxMarks } : c)));
  }, [rubric, setRubric]);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
          <TextField
            size="small"
            label="Round name"
            fullWidth
            value={round.name}
            onChange={(e) => onChange(index, { name: e.target.value })}
            placeholder={round.kind === 'observation' ? 'e.g. Child observation' : 'e.g. Parent interview'}
          />
          <TextField
            size="small"
            label="Total marks"
            type="number"
            inputProps={{ min: 1 }}
            value={roundMax(round) || ''}
            onChange={(e) => onChange(index, { maxMarks: Number(e.target.value) || 0 })}
            disabled={scored}
            helperText={scored ? 'Sum of the scored questions' : ' '}
            sx={{ width: 150, flexShrink: 0 }}
          />
          <Stack direction="row" sx={{ mt: 0.5, flexShrink: 0 }}>
            <Tooltip title="Duplicate round">
              <IconButton size="small" onClick={() => onDuplicate(index)}>
                <ContentCopyOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove round">
              <IconButton size="small" color="error" onClick={() => onRemove(index)}>
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary"><HelpTip label="Type" {...TYPE_HELP} /></Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={round.kind === 'observation' ? 'observation' : 'interview'}
              onChange={(_, v) => v && onChange(index, { kind: v })}
              aria-label="Round type"
              sx={segmentedSx}
            >
              <ToggleButton value="interview">Interview</ToggleButton>
              <ToggleButton value="observation">Observation</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary"><HelpTip label="Marks" {...MARKS_HELP} /></Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={round.markStep === 0.5 ? 0.5 : 1}
              onChange={(_, v) => v && onChange(index, { markStep: v })}
              aria-label="Mark step"
              sx={segmentedSx}
            >
              <ToggleButton value={1}>Whole</ToggleButton>
              <ToggleButton value={0.5}>Halves</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>

        <Divider />

        <GroupLabel
          count={rubric.length || undefined}
          action={
            <Button size="small" startIcon={<Add fontSize="small" />} onClick={() => setRubric([...rubric, { criterion: '', kind: 'marks', maxMarks: 10 }])}>
              Add question
            </Button>
          }
        >
          Questions
        </GroupLabel>

        {rubric.length === 0 ? (
          <Typography variant="body2" color="text.disabled">
            No questions — the round is marked as a single total.
          </Typography>
        ) : (
          <Box>
            {rubric.map((c, j) => (
              <RubricItemRow
                key={j}
                item={c}
                index={j}
                count={rubric.length}
                canCopyMarks={scoredCount > 1}
                onChange={changeItem}
                onMove={moveItem}
                onCopyMarks={copyMarks}
                onRemove={removeItem}
              />
            ))}
          </Box>
        )}
      </Stack>
    </Paper>
  );
});

InterviewRoundEditor.displayName = 'InterviewRoundEditor';

export default InterviewRoundEditor;
